import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Table, Modal, Button } from 'react-bootstrap';
import Sidebar from '../components/SideBar';
import logoImage from '../images/Logo-Base-basic.png';
import TextField from '@mui/material/TextField';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ENDPOINT_PATH } from '../config';
import { authFetch } from '../utils/authFetch';
import './users.css';
import '../App.css';
import { getAllUserInformation } from './usersHelper';

function tableColumnAriaSort(activeKey, activeDir, columnKey) {
  if (activeKey !== columnKey) return 'none';
  return activeDir === 'asc' ? 'ascending' : 'descending';
}

function UserSortHeaderButton({ label, sortKey, activeKey, activeDir, onSort }) {
  const isPrimary = activeKey === sortKey;
  const showNeutral = !isPrimary;
  let arrows;
  if (showNeutral) {
    arrows = (
      <span className="users-sort-arrows users-sort-arrows--neutral" aria-hidden>
        <span className="users-sort-arrow-up">▲</span>
        <span className="users-sort-arrow-down">▼</span>
      </span>
    );
  } else if (activeDir === 'asc') {
    arrows = (
      <span className="users-sort-arrows" aria-hidden>
        <span className="users-sort-arrow-up users-sort-arrow-up--solo">▲</span>
      </span>
    );
  } else {
    arrows = (
      <span className="users-sort-arrows" aria-hidden>
        <span className="users-sort-arrow-down users-sort-arrow-down--solo">▼</span>
      </span>
    );
  }
  return (
    <button type="button" className="users-sort-btn" onClick={() => onSort(sortKey)}>
      <span>{label}</span>
      {arrows}
    </button>
  );
}

function userFieldString(row, fieldKey) {
  const v = row[fieldKey];
  return String(v ?? '').trim();
}

function compareUserStringsForSort(a, b, fieldKey, ascending) {
  const sa = userFieldString(a, fieldKey);
  const sb = userFieldString(b, fieldKey);
  const aEmpty = sa === '';
  const bEmpty = sb === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const cmp = sa.localeCompare(sb, undefined, { sensitivity: 'base', numeric: true });
  return ascending ? cmp : -cmp;
}

function compareUsersForTable(a, b, primaryKey, primaryDir) {
  const asc = primaryDir === 'asc';
  const strCmp = compareUserStringsForSort(a, b, primaryKey, asc);
  if (strCmp !== 0) return strCmp;
  if (primaryKey === 'type') {
    const firstNameCmp = compareUserStringsForSort(a, b, 'firstName', true);
    if (firstNameCmp !== 0) return firstNameCmp;
  }
  return String(b.email || '').localeCompare(String(a.email || ''));
}

function sortUserRows(list, primaryKey, primaryDir) {
  return [...list].sort((x, y) => compareUsersForTable(x, y, primaryKey, primaryDir));
}

const AdminNavbar = () => {
  return (
    <div className="navWrapper">
      <div className="AdminNav">
        <div>
          <a href="/" className="NavLink">
            <div className="AdminLogo d-flex justify-content-end">
              <img src={logoImage} alt="logo" className="NavLogo" />
            </div>
            <div className="AdminLogoText font-2 ps-2 fontLogo">mycustomframe</div>
          </a>
        </div>
      </div>
    </div>
  );
};

const UserPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [users, setUsers] = useState([]);
  const itemsPerPage = 8;
  const [userSort, setUserSort] = useState({ key: 'firstName', dir: 'asc' });
  
  // User type detection for read-only mode
  const [userType, setUserType] = useState(null);
  
  // Employee creation modal states
  const [showCreateEmployeeModal, setShowCreateEmployeeModal] = useState(false);
  const [newEmployeeEmail, setNewEmployeeEmail] = useState('');
  const [newEmployeePassword, setNewEmployeePassword] = useState('');
  const [newEmployeeFirstName, setNewEmployeeFirstName] = useState('');
  const [newEmployeeLastName, setNewEmployeeLastName] = useState('');
  const [createEmployeeLoading, setCreateEmployeeLoading] = useState(false);
  const [createEmployeeError, setCreateEmployeeError] = useState('');
  
  const user = JSON.parse(localStorage.getItem('user'));
  
  // Fetch user type on mount
  useEffect(() => {
    const fetchUserType = async () => {
      try {
        if (user && user.uid) {
          const response = await fetch(ENDPOINT_PATH + `/api/get-account-type?user=` + encodeURIComponent(user.uid));
          const accountType = await response.json();
          setUserType(accountType.toLowerCase());
        }
      } catch (error) {
        console.error("Error fetching user type:", error);
      }
    };
    
    fetchUserType();
  }, [user]);
  
  useEffect(() => {
    getAllUserInformation().then(users => {
      setUsers(users);
    });
  }, []);

  const filteredUsers = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const base = (users || []).filter((u) =>
      [u.firstName, u.lastName, u.email, u.type]
        .map((field) => (field?.toLowerCase() || ''))
        .some((value) => value.includes(q))
    );
    return sortUserRows(base, userSort.key, userSort.dir);
  }, [users, searchQuery, userSort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));
    setCurrentPage((p) => Math.min(Math.max(1, p), maxPage));
  }, [filteredUsers.length, itemsPerPage]);

  const handleUserSortClick = useCallback((clickedKey) => {
    setCurrentPage(1);
    setUserSort((prev) => {
      if (prev.key === clickedKey) {
        return { key: clickedKey, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key: clickedKey, dir: 'asc' };
    });
  }, []);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));

  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const paginatedUsers = filteredUsers.slice(
    (safePage - 1) * itemsPerPage,
    safePage * itemsPerPage
  );

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  
  const handleCreateEmployee = async () => {
    setCreateEmployeeLoading(true);
    setCreateEmployeeError('');
    
    try {
      const response = await authFetch(ENDPOINT_PATH + '/api/admin/create-employee', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newEmployeeEmail,
          password: newEmployeePassword,
          firstName: newEmployeeFirstName,
          lastName: newEmployeeLastName
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert(`Employee account created successfully!\n\nEmail: ${data.email}\nPassword: ${newEmployeePassword}\n\nPlease save these credentials and share with the employee.`);
        
        // Reset form
        setNewEmployeeEmail('');
        setNewEmployeePassword('');
        setNewEmployeeFirstName('');
        setNewEmployeeLastName('');
        setShowCreateEmployeeModal(false);
        
        // Refresh user list
        const updatedUsers = await getAllUserInformation();
        setUsers(updatedUsers);
      } else {
        setCreateEmployeeError(data.message || 'Failed to create employee');
      }
    } catch (error) {
      console.error('Error creating employee:', error);
      setCreateEmployeeError('Error creating employee account');
    } finally {
      setCreateEmployeeLoading(false);
    }
  };
  
  const handleCloseEmployeeModal = () => {
    setShowCreateEmployeeModal(false);
    setNewEmployeeEmail('');
    setNewEmployeePassword('');
    setNewEmployeeFirstName('');
    setNewEmployeeLastName('');
    setCreateEmployeeError('');
  };

  return (
    <div>
      <AdminNavbar />
      <hr className="admin-divider" />
      <div className="user-page">
        <Sidebar userType={userType || 'admin'} />
        <div className="user-content">
          {/* Line 1: View Only Banner (centered) */}
          {userType === 'employee' && (
            <div className="view-only-banner">
              <VisibilityIcon style={{ fontSize: '18px' }} />
              <span>Employee accounts have read-only access to user information.</span>
            </div>
          )}
          
          {/* Line 2: Button (left) + Search (right) */}
          <div className="header-row">
            <div className="header-left">
              {userType === 'admin' && (
                <Button 
                  variant="primary" 
                  onClick={() => setShowCreateEmployeeModal(true)}
                  style={{backgroundColor: '#ff4172', borderColor: '#ff4172'}}
                >
                  + Create Employee Account
                </Button>
              )}
            </div>
            
            <input
              type="text"
              className="search-box"
              placeholder="Search User..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div>
            <div className="user-table-container users-sortable-wrap">
                <Table striped bordered hover className="user-table">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        aria-sort={tableColumnAriaSort(userSort.key, userSort.dir, 'firstName')}
                        className={`users-sort-th ${userSort.key === 'firstName' ? 'users-sort-th--active' : ''}`}
                      >
                        <UserSortHeaderButton
                          label="FIRST NAME"
                          sortKey="firstName"
                          activeKey={userSort.key}
                          activeDir={userSort.dir}
                          onSort={handleUserSortClick}
                        />
                      </th>
                      <th
                        scope="col"
                        aria-sort={tableColumnAriaSort(userSort.key, userSort.dir, 'lastName')}
                        className={`users-sort-th ${userSort.key === 'lastName' ? 'users-sort-th--active' : ''}`}
                      >
                        <UserSortHeaderButton
                          label="LAST NAME"
                          sortKey="lastName"
                          activeKey={userSort.key}
                          activeDir={userSort.dir}
                          onSort={handleUserSortClick}
                        />
                      </th>
                      <th
                        scope="col"
                        aria-sort={tableColumnAriaSort(userSort.key, userSort.dir, 'email')}
                        className={`users-sort-th ${userSort.key === 'email' ? 'users-sort-th--active' : ''}`}
                      >
                        <UserSortHeaderButton
                          label="EMAIL"
                          sortKey="email"
                          activeKey={userSort.key}
                          activeDir={userSort.dir}
                          onSort={handleUserSortClick}
                        />
                      </th>
                      <th
                        scope="col"
                        aria-sort={tableColumnAriaSort(userSort.key, userSort.dir, 'type')}
                        className={`users-sort-th ${userSort.key === 'type' ? 'users-sort-th--active' : ''}`}
                      >
                        <UserSortHeaderButton
                          label="TYPE"
                          sortKey="type"
                          activeKey={userSort.key}
                          activeDir={userSort.dir}
                          onSort={handleUserSortClick}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((u) => (
                    <tr key={`${u.email}-${u.type}-${u.firstName}-${u.lastName}`}>
                    <td style={{ color: u.type === 'admin' ? '#ff4172' : 'inherit' }}>{u.firstName}</td>
                    <td style={{ color: u.type === 'admin' ? '#ff4172' : 'inherit' }}>{u.lastName}</td>
                    <td style={{ color: u.type === 'admin' ? '#ff4172' : 'inherit' }}>{u.email}</td>
                    <td style={{ color: u.type === 'admin' ? '#ff4172' : 'inherit' }}>{u.type}</td>
                    </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="pagination">
                <button
                  className="page-btn"
                  onClick={() => handlePageChange(safePage - 1)}
                  disabled={safePage <= 1 || filteredUsers.length === 0}
                >
                  Prev
                </button>
                <span>
                  {filteredUsers.length === 0
                    ? 'Showing 0 - 0 of 0'
                    : `Showing ${(safePage - 1) * itemsPerPage + 1} - ${Math.min(
                    safePage * itemsPerPage,
                    filteredUsers.length
                  )} of ${filteredUsers.length}`}
                </span>
                <button
                  className="page-btn"
                  onClick={() => handlePageChange(safePage + 1)}
                  disabled={safePage >= totalPages || filteredUsers.length === 0}
                >
                  Next
                </button>
              </div>
            </div> 
        </div>
      </div>
      
      {/* Employee Creation Modal */}
      <Modal show={showCreateEmployeeModal} onHide={handleCloseEmployeeModal}>
        <Modal.Header closeButton style={{backgroundColor: '#ff4172', color: 'white'}}>
          <Modal.Title>Create Employee Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <TextField
              fullWidth
              label="First Name"
              value={newEmployeeFirstName}
              onChange={(e) => setNewEmployeeFirstName(e.target.value)}
              variant="outlined"
              required
            />
          </div>
          <div className="mb-3">
            <TextField
              fullWidth
              label="Last Name"
              value={newEmployeeLastName}
              onChange={(e) => setNewEmployeeLastName(e.target.value)}
              variant="outlined"
              required
            />
          </div>
          <div className="mb-3">
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newEmployeeEmail}
              onChange={(e) => setNewEmployeeEmail(e.target.value)}
              variant="outlined"
              required
            />
          </div>
          <div className="mb-3">
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={newEmployeePassword}
              onChange={(e) => setNewEmployeePassword(e.target.value)}
              variant="outlined"
              required
              helperText="Minimum 6 characters"
            />
          </div>
          {createEmployeeError && (
            <div className="alert alert-danger">{createEmployeeError}</div>
          )}
          <div className="alert alert-info">
            <small>
              <strong>ℹ️ Note:</strong> The employee will be able to login immediately with these credentials.
              Make sure to save and share the password securely.
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEmployeeModal}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateEmployee}
            disabled={createEmployeeLoading || !newEmployeeEmail || !newEmployeePassword || !newEmployeeFirstName || !newEmployeeLastName || newEmployeePassword.length < 6}
            style={{backgroundColor: '#ff4172', borderColor: '#ff4172'}}
          >
            {createEmployeeLoading ? 'Creating...' : 'Create Employee'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserPage;
